import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';

const BACKGROUND_IMAGE = '/update-vaqueiro.png';

const featuresList = [
  {
    key: 'decisions',
    icon: CheckCircleIcon,
  },
  {
    key: 'efficiency',
    icon: TrendingUpIcon,
  },
  {
    key: 'results',
    icon: SpeedIcon,
  },
];

export const IntelligenceSection: React.FC = () => {
  const { t } = useTranslation(['landing']);
  return (
    <Box
      component="section"
      id="solutions"
      sx={{
        position: 'relative',
        width: '100%',
        backgroundColor: '#F1F4F3',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        minHeight: { xs: 'auto', md: '731px' },
      }}
    >
      {/* Background Image Container */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: { xs: '100%', md: '65%' },
          display: { xs: 'none', md: 'block' },
        }}
      >
        <img 
          src={BACKGROUND_IMAGE} 
          alt="" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }} 
        />
        {/* Gradient fade to blend image with solid background on the left */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, #F1F4F3 0%, rgba(241, 244, 243, 0.98) 15%, rgba(241, 244, 243, 0) 35%)',
          }}
        />
      </Box>

      {/* Content Container */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 1280,
          mx: 'auto',
          py: { xs: 8, md: 12 },
          px: { xs: 3, md: 6 },
        }}
      >
        <Box
          sx={{
            maxWidth: '650px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Headlines */}
          <Box sx={{ mb: 6 }}>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 600,
                fontSize: { xs: '36px', md: '72px' },
                lineHeight: { xs: '40px', md: '72px' },
                letterSpacing: '-1.8px',
                color: '#004E64',
              }}
            >
              {t('landing:intelligence_new.title_line1')}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '36px', md: '72px' },
                lineHeight: { xs: '40px', md: '72px' },
                letterSpacing: '-1.8px',
                color: 'rgba(0, 78, 100, 0.6)',
              }}
            >
              {t('landing:intelligence_new.title_line2')}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 600,
                fontSize: { xs: '36px', md: '72px' },
                lineHeight: { xs: '40px', md: '72px' },
                letterSpacing: '-1.8px',
                color: '#004E64',
              }}
            >
              {t('landing:intelligence_new.title_line3')}
            </Typography>
          </Box>

          {/* Features List */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {featuresList.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    pb: index !== featuresList.length - 1 ? 3 : 0,
                    borderBottom: index !== featuresList.length - 1 ? '1px solid rgba(0, 78, 100, 0.1)' : 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 0.5,
                      '& svg': {
                        color: '#004E64',
                        fontSize: 24,
                      },
                    }}
                  >
                    <IconComponent />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: '"New Order", sans-serif',
                        fontWeight: 600,
                        fontSize: '18px',
                        color: '#003D4D',
                        mb: 0.5,
                      }}
                    >
                      {t(`landing:intelligence_new.features.${feature.key}.title`)}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#41484B',
                      }}
                    >
                      {t(`landing:intelligence_new.features.${feature.key}.description`)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* CTA Banner */}
          <Box
            sx={{
              mt: 6,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              p: 2,
              pr: 4,
              boxShadow: '0px 10px 30px rgba(0, 78, 100, 0.05)',
              alignSelf: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: 'rgba(140, 243, 243, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': {
                  color: '#004E64',
                  fontSize: 24,
                },
              }}
            >
              <GroupsIcon />
            </Box>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                color: '#003D4D',
              }}
            >
              {t('landing:intelligence_new.cta')}
            </Typography>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};